// frontend/src/store/settingsStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import settingsAPI from '../services/settings.api';

const useSettingsStore = create(
  persist(
    (set, get) => ({
      settings: null,
      isLoading: false,
      error: null,
      sessions: [],

      // Load settings
      loadSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.getSettings();
          set({ settings: response.data, isLoading: false });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to load settings';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Update notification settings
      updateNotificationSettings: async (notificationSettings) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.updateNotificationSettings(notificationSettings);
          set((state) => ({
            settings: { ...state.settings, ...response.data },
            isLoading: false,
          }));
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to update settings';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Two-factor authentication
      setupTwoFactor: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.setupTwoFactor();
          set({ isLoading: false });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to setup 2FA';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      verifyTwoFactor: async (code) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.verifyTwoFactor(code);
          set((state) => ({
            settings: { ...state.settings, twoFactorEnabled: response.data.enabled },
            isLoading: false,
          }));
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to verify 2FA';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      disableTwoFactor: async (backupCode) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.disableTwoFactor(backupCode);
          set((state) => ({
            settings: { ...state.settings, twoFactorEnabled: false },
            isLoading: false,
          }));
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to disable 2FA';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Session management
      loadSessions: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.getSessions();
          set({ sessions: response.data, isLoading: false });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to load sessions';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      revokeSession: async (sessionId) => {
        set({ isLoading: true, error: null });
        try {
          await settingsAPI.revokeSession(sessionId);
          set((state) => ({
            sessions: state.sessions.filter(s => s.id !== sessionId),
            isLoading: false,
          }));
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to revoke session';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      revokeAllSessions: async (password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.revokeAllSessions(password);
          set({ sessions: [], isLoading: false });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to revoke sessions';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Data management
      exportData: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.exportData();
          set({ isLoading: false });

          // Create download link
          const dataStr = JSON.stringify(response.data, null, 2);
          const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
          const exportFileDefaultName = `fastel-data-${new Date().toISOString()}.json`;

          const linkElement = document.createElement('a');
          linkElement.setAttribute('href', dataUri);
          linkElement.setAttribute('download', exportFileDefaultName);
          linkElement.click();

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to export data';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      requestAccountDeletion: async (password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.requestAccountDeletion(password);
          set({ isLoading: false });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to request deletion';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Reset settings
      resetSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await settingsAPI.resetSettings();
          set({ settings: response.data, isLoading: false });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to reset settings';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        settings: state.settings,
      }),
    }
  )
);

export default useSettingsStore;